import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import FormHelperText from '@mui/material/FormHelperText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { Client, CreateProjectDto, Project, ProjectStatus, UpdateProjectDto } from 'api';
import React, { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { statusMenuItems, StatusOption } from 'shared/components/StatusSelect';
import { PROJECT_COLORS } from 'shared/constants/colors';
import { z } from 'zod';

import {
  ColorSwatch,
  ColorSwatchRow,
  DialogCloseButton,
  FieldLabel,
  formDialogPaperProps,
  FormCancelButton,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormFieldStack,
  FormSubmitButton,
  FormSwitch,
  FormTextField,
  RequiredMark,
  ToggleRow,
  ToggleStack,
} from '../projects.styles';

import { ClientSelect } from './ClientSelect';

const projectSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  color: z.string().min(1, 'Color is required'),
  clientId: z.string().optional(),
  note: z.string().max(2000).optional(),
  status: z.enum(['Active', 'Inactive', 'OnHold']),
  isPublic: z.boolean(),
  isBillable: z.boolean(),
  billableRate: z.preprocess(
    (v) => (v == null || (typeof v === 'number' && isNaN(v)) || v === '' ? undefined : Number(v)),
    z.number().min(0).optional(),
  ),
  estimatedHours: z.preprocess(
    (v) => (v == null || (typeof v === 'number' && isNaN(v)) || v === '' ? undefined : Number(v)),
    z.number().min(0).optional(),
  ),
});

type ProjectFormValues = z.infer<typeof projectSchema>;

interface ProjectFormModalProps {
  open: boolean;
  project?: Project | null;
  clients: Client[];
  onClose: () => void;
  onSubmit: (data: CreateProjectDto | UpdateProjectDto) => void;
  isPending?: boolean;
}

export const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  open,
  project,
  clients,
  onClose,
  onSubmit,
  isPending = false,
}) => {
  const isEdit = Boolean(project);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: '',
      color: PROJECT_COLORS[0],
      clientId: '',
      note: '',
      status: 'Active' as ProjectStatus,
      isPublic: false,
      isBillable: false,
      billableRate: NaN,
      estimatedHours: NaN,
    },
  });

  const isBillable = useWatch({ control, name: 'isBillable' });
  const selectedColor = useWatch({ control, name: 'color' });

  useEffect(() => {
    if (!open) return;
    if (project) {
      reset({
        name: project.name,
        color: project.color || PROJECT_COLORS[0],
        clientId: project.clientId ?? '',
        note: project.note ?? '',
        status: project.status,
        isPublic: project.isPublic,
        isBillable: project.isBillable,
        billableRate: project.billableRate ?? NaN,
        estimatedHours: project.estimatedHours ?? NaN,
      });
    } else {
      reset({
        name: '',
        color: PROJECT_COLORS[0],
        clientId: '',
        note: '',
        status: 'Active',
        isPublic: false,
        isBillable: false,
        billableRate: NaN,
        estimatedHours: NaN,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFormSubmit = (values: ProjectFormValues) => {
    const payload: CreateProjectDto | UpdateProjectDto = {
      name: values.name,
      color: values.color,
      clientId: values.clientId || undefined,
      note: values.note || undefined,
      ...(isEdit && { status: values.status }),
      isPublic: values.isPublic,
      isBillable: values.isBillable,
      billableRate: values.billableRate,
      estimatedHours: values.estimatedHours,
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={formDialogPaperProps}>
      <DialogCloseButton onClick={onClose} aria-label="Close">
        <CloseIcon sx={{ fontSize: 16 }} />
      </DialogCloseButton>

      <FormDialogTitle>{isEdit ? 'Edit Project' : 'Create new project'}</FormDialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <FormDialogContent>
          <FormFieldStack spacing={2.5}>
            <div>
              <FieldLabel>
                Name <RequiredMark>*</RequiredMark>
              </FieldLabel>
              <FormTextField
                size="small"
                placeholder="Project name"
                required
                autoFocus
                fullWidth
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </div>

            <div>
              <FieldLabel sx={{ mb: 1 }}>Color</FieldLabel>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <ColorSwatchRow>
                    {PROJECT_COLORS.map((c) => (
                      <Tooltip key={c} title={c}>
                        <ColorSwatch
                          swatchcolor={c}
                          selected={selectedColor === c}
                          onClick={() => field.onChange(c)}
                        />
                      </Tooltip>
                    ))}
                  </ColorSwatchRow>
                )}
              />
              {errors.color && <FormHelperText error>{errors.color.message}</FormHelperText>}
            </div>

            <div>
              <FieldLabel>Client</FieldLabel>
              <Controller
                name="clientId"
                control={control}
                render={({ field }) => (
                  <ClientSelect
                    clients={clients}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div>
              <FieldLabel>Note</FieldLabel>
              <FormTextField
                size="small"
                placeholder="Add a note about this project..."
                fullWidth
                multiline
                rows={3}
                error={Boolean(errors.note)}
                helperText={errors.note?.message}
                {...register('note')}
              />
            </div>

            {isEdit && (
              <div>
                <FieldLabel>Status</FieldLabel>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormTextField
                      {...field}
                      select
                      size="small"
                      fullWidth
                      slotProps={{
                        select: {
                          renderValue: (val) => <StatusOption value={val as string} />,
                        },
                      }}
                    >
                      {statusMenuItems()}
                    </FormTextField>
                  )}
                />
              </div>
            )}

            <div>
              <FieldLabel>Estimated Hours</FieldLabel>
              <FormTextField
                size="small"
                placeholder="e.g. 120"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 0.5 }}
                error={Boolean(errors.estimatedHours)}
                helperText={errors.estimatedHours?.message}
                {...register('estimatedHours', { valueAsNumber: true })}
              />
            </div>

            <ToggleStack>
              <Controller
                name="isPublic"
                control={control}
                render={({ field }) => (
                  <ToggleRow
                    control={
                      <FormSwitch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Public — visible to all workspace members
                      </Typography>
                    }
                  />
                )}
              />

              <Controller
                name="isBillable"
                control={control}
                render={({ field }) => (
                  <ToggleRow
                    control={
                      <FormSwitch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Billable</Typography>}
                  />
                )}
              />
            </ToggleStack>

            {isBillable && (
              <div>
                <FieldLabel>Billable Rate (per hour)</FieldLabel>
                <FormTextField
                  size="small"
                  placeholder="e.g. 50"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  error={Boolean(errors.billableRate)}
                  helperText={errors.billableRate?.message}
                  {...register('billableRate', { valueAsNumber: true })}
                />
              </div>
            )}
          </FormFieldStack>
        </FormDialogContent>

        <FormDialogActions>
          <FormCancelButton onClick={onClose} disabled={isPending}>
            Cancel
          </FormCancelButton>
          <FormSubmitButton
            type="submit"
            variant="contained"
            disableElevation
            disabled={isPending || (isEdit && !isDirty)}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isEdit ? 'Save Changes' : 'Create Project'}
          </FormSubmitButton>
        </FormDialogActions>
      </form>
    </Dialog>
  );
};
